import { RefObject, useEffect, useRef } from 'react';

const PLATFORM_HEADER_SELECTOR = '.PlatformHeader';

interface iScrollToResultsOnLoadOptions {
    /** True when the page was opened with an active filter in the URL. Read once, on mount. */
    enabled: boolean;
    /** True once everything rendered above the target has finished loading, so the target's position is stable. */
    isReady: boolean;
}

/**
 * Scrolls a page's filtered-results section to sit just under the platform header, once, when the
 * page is opened with a filter already in the URL (a tag link from an article, a shared link, …).
 *
 * Without this, on shorter viewports only the hero above the results is visible after the
 * navigation, and the hero never changes with the filter — so the click looks like it did nothing.
 *
 * Filters the user changes after mount never re-trigger the scroll: `enabled` is captured once.
 */
export default function useScrollToResultsOnLoad(
    targetRef: RefObject<HTMLElement>,
    { enabled, isReady }: iScrollToResultsOnLoadOptions
) {
    const shouldScroll = useRef(enabled);
    const hasScrolled = useRef(false);

    useEffect(() => {
        if (!shouldScroll.current || hasScrolled.current || !isReady) return;

        // Next frame, so content that has just finished loading above the target is laid out first
        const frame = requestAnimationFrame(() => {
            const target = targetRef.current;
            if (!target) return;
            hasScrolled.current = true;

            // The platform header is fixed, so its bottom edge is how far the target must stop short of the top
            const header = document.querySelector<HTMLElement>(PLATFORM_HEADER_SELECTOR);
            target.style.scrollMarginTop = `${header ? header.getBoundingClientRect().bottom : 0}px`;

            const prefersReducedMotion =
                typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            target.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' });
        });

        return () => cancelAnimationFrame(frame);
    }, [targetRef, isReady]);
}
